import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Package, Search, X, Plus, Edit, Trash, Lightbulb } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/currencyUtils';

interface TrackPricing {
  id: string;
  track_type: 'drag' | 'drift';
  title: string;
  price: number;
  description: string;
  duration_hours: number;
  use_traction_compound: boolean;
  is_active: boolean;
  deposit_percentage: number;
  deposit_amount: number;
  blocks_full_day: boolean;
}

interface TrackInfo {
  id: string;
  track_type: 'drag' | 'drift';
  title: string;
  description: string;
  features: string[];
  is_active: boolean;
}

interface AddOnService {
  id: string;
  name: string;
  description: string;
  price: number;
  is_active: boolean;
}

interface SystemSetting {
  id: string;
  setting_key: string;
  setting_value: string;
  description: string;
}

export default function ServiceManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pricing, setPricing] = useState<TrackPricing[]>([]);
  const [trackInfo, setTrackInfo] = useState<TrackInfo | null>(null);
  const [addOns, setAddOns] = useState<AddOnService[]>([]);
  const [systemSettings, setSystemSettings] = useState<SystemSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showTrackInfoModal, setShowTrackInfoModal] = useState(false);
  const [showAddOnModal, setShowAddOnModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedPricing, setSelectedPricing] = useState<TrackPricing | null>(null);
  const [selectedAddOn, setSelectedAddOn] = useState<AddOnService | null>(null);
  const [selectedTrackType, setSelectedTrackType] = useState<'drag' | 'drift'>('drag');
  const [pricingForm, setPricingForm] = useState({
    title: '',
    price: 0,
    description: '',
    duration_hours: 1,
    use_traction_compound: false,
    is_active: true,
    track_type: 'drag' as 'drag' | 'drift',
    deposit_percentage: 30,
    blocks_full_day: false
  });
  const [trackInfoForm, setTrackInfoForm] = useState({
    title: '',
    description: '',
    features: [''],
    is_active: true
  });
  const [addOnForm, setAddOnForm] = useState({
    name: '',
    description: '',
    price: 0,
    is_active: true
  });
  
  // Local state for system settings form
  const [settingsForm, setSettingsForm] = useState({
    lighting_fee_amount: '',
    lighting_fee_enabled: false,
    lighting_fee_threshold: ''
  });

  useEffect(() => {
    fetchPricing();
    fetchTrackInfo();
    fetchAddOns();
    fetchSystemSettings();
  }, [user, selectedTrackType]);

  const fetchPricing = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('track_pricing')
        .select('*')
        .eq('track_type', selectedTrackType)
        .order('duration_hours', { ascending: true });

      if (error) throw error;
      setPricing(data || []);
    } catch (error) {
      console.error('Error fetching pricing:', error);
      toast.error('Failed to load Services');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrackInfo = async () => {
    try {
      const { data, error } = await supabase
        .from('track_info')
        .select('*')
        .eq('track_type', selectedTrackType)
        .single();

      if (error) throw error;
      setTrackInfo(data);
      setTrackInfoForm({
        title: data.title,
        description: data.description,
        features: data.features,
        is_active: data.is_active
      });
    } catch (error) {
      console.error('Error fetching track info:', error);
      toast.error('Failed to load track information');
    }
  };

  const fetchAddOns = async () => {
    try {
      const { data, error } = await supabase
        .from('add_on_services')
        .select('*')
        .order('name');

      if (error) throw error;
      setAddOns(data || []);
    } catch (error) {
      console.error('Error fetching add-ons:', error);
      toast.error('Failed to load add-on services');
    }
  };

  const fetchSystemSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('*')
        .order('setting_key');

      if (error) throw error;
      setSystemSettings(data || []);
      
      // Populate form with current values
      const settingsMap = (data || []).reduce((acc, setting) => {
        acc[setting.setting_key] = setting.setting_value;
        return acc;
      }, {} as Record<string, string>);
      
      setSettingsForm({
        lighting_fee_amount: settingsMap.lighting_fee_amount || '50.00',
        lighting_fee_enabled: settingsMap.lighting_fee_enabled === 'true',
        lighting_fee_threshold: settingsMap.lighting_fee_threshold || '18:00'
      });
    } catch (error) {
      console.error('Error fetching system settings:', error);
      toast.error('Failed to load system settings');
    }
  };

  const handlePricingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const formData = {
        ...pricingForm,
        track_type: selectedTrackType,
        blocks_full_day: pricingForm.duration_hours === 8 || pricingForm.blocks_full_day
      };

      if (selectedPricing) {
        const { error } = await supabase
          .from('track_pricing')
          .update(formData)
          .eq('id', selectedPricing.id);

        if (error) throw error;
        toast.success('services updated successfully');
      } else {
        const { error } = await supabase
          .from('track_pricing')
          .insert([formData]);

        if (error) throw error;
        toast.success('Services created successfully');
      }

      setShowPricingModal(false);
      setSelectedPricing(null);
      setPricingForm({
        title: '',
        price: 0,
        description: '',
        duration_hours: 1,
        use_traction_compound: false,
        is_active: true,
        track_type: 'drag',
        deposit_percentage: 30,
        blocks_full_day: false
      });
      fetchPricing();
    } catch (error) {
      console.error('Error saving service option:', error);
      toast.error('Failed to save service option');
    }
  };

  const handleTrackInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from('track_info')
        .update({
          title: trackInfoForm.title,
          description: trackInfoForm.description,
          features: trackInfoForm.features,
          is_active: trackInfoForm.is_active
        })
        .eq('track_type', selectedTrackType);

      if (error) throw error;
      toast.success('Track information updated successfully');
      setShowTrackInfoModal(false);
      fetchTrackInfo();
    } catch (error) {
      console.error('Error updating track info:', error);
      toast.error('Failed to update track information');
    }
  };

  const handleAddOnSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (selectedAddOn) {
        const { error } = await supabase
          .from('add_on_services')
          .update(addOnForm)
          .eq('id', selectedAddOn.id);

        if (error) throw error;
        toast.success('Add-on service updated successfully');
      } else {
        const { error } = await supabase
          .from('add_on_services')
          .insert([addOnForm]);

        if (error) throw error;
        toast.success('Add-on service created successfully');
      }

      setShowAddOnModal(false);
      setSelectedAddOn(null);
      setAddOnForm({
        name: '',
        description: '',
        price: 0,
        is_active: true
      });
      fetchAddOns();
    } catch (error) {
      console.error('Error saving add-on service:', error);
      toast.error('Failed to save add-on service');
    }
  };

  const handleSystemSettingsSubmit = async () => {
    try {
      const updates = [
        { setting_key: 'lighting_fee_amount', setting_value: settingsForm.lighting_fee_amount },
        { setting_key: 'lighting_fee_enabled', setting_value: settingsForm.lighting_fee_enabled.toString() },
        { setting_key: 'lighting_fee_threshold', setting_value: settingsForm.lighting_fee_threshold }
      ];

      for (const update of updates) {
        const { error } = await supabase
          .from('system_settings')
          .update({ setting_value: update.setting_value })
          .eq('setting_key', update.setting_key);

        if (error) throw error;
      }

      toast.success('Lighting settings updated successfully');
      setShowSettingsModal(false);
      fetchSystemSettings();
    } catch (error) {
      console.error('Error updating system settings:', error);
      toast.error('Failed to update lighting settings');
    }
  };

  const handleDeletePricing = async (id: string) => {
    if (!confirm('Are you sure you want to delete this service option?')) return;

    try {
      const { error } = await supabase
        .from('track_pricing')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Service option deleted successfully');
      fetchPricing();
    } catch (error) {
      console.error('Error deleting service option:', error);
      toast.error('Failed to delete service option');
    }
  };

  const handleDeleteAddOn = async (id: string) => {
    if (!confirm('Are you sure you want to delete this add-on service?')) return;

    try {
      const { error } = await supabase
        .from('add_on_services')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Add-on service deleted successfully');
      fetchAddOns();
    } catch (error) {
      console.error('Error deleting add-on service:', error);
      toast.error('Failed to delete add-on service');
    }
  };

  const addFeature = () => {
    setTrackInfoForm({
      ...trackInfoForm,
      features: [...trackInfoForm.features, '']
    });
  };

  const removeFeature = (index: number) => {
    setTrackInfoForm({
      ...trackInfoForm,
      features: trackInfoForm.features.filter((_, i) => i !== index)
    });
  };

  const updateFeature = (index: number, value: string) => {
    const newFeatures = [...trackInfoForm.features];
    newFeatures[index] = value;
    setTrackInfoForm({
      ...trackInfoForm,
      features: newFeatures
    });
  };

  const filteredPricing = pricing.filter(item =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAddOns = addOns.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">Track Management</h1>
          <div className="flex gap-4">
            <button
              onClick={() => setShowSettingsModal(true)}
              className="bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Lightbulb className="h-5 w-5" />
              Lighting Settings
            </button>
            <button
              onClick={() => setShowTrackInfoModal(true)}
              className="bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Edit className="h-5 w-5" />
              Edit Track Info
            </button>
          </div>
        </div>

        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setSelectedTrackType('drag')}
            className={`px-4 py-2 rounded-lg ${
              selectedTrackType === 'drag'
                ? 'bg-primary-600 text-white'
                : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
            }`}
          >
            Drag Strip
          </button>
          <button
            onClick={() => setSelectedTrackType('drift')}
            className={`px-4 py-2 rounded-lg ${
              selectedTrackType === 'drift'
                ? 'bg-primary-600 text-white'
                : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
            }`}
          >
            Drift Track
          </button>
        </div>

        {trackInfo && (
          <div className="bg-zinc-900 rounded-lg p-6 mb-8">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">{trackInfo.title}</h2>
                <p className="text-gray-400 mb-4">{trackInfo.description}</p>
                <ul className="space-y-2">
                  {trackInfo.features.map((feature, index) => (
                    <li key={index} className="text-gray-400">• {feature}</li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => setShowTrackInfoModal(true)}
                className="text-gray-400 hover:text-white"
              >
                <Edit className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search service options and add-ons..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-zinc-900 text-white pl-10 pr-4 py-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
          />
        </div>

        <div className="mb-12">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">Service Options</h2>
            <button
              onClick={() => {
                setSelectedPricing(null);
                setPricingForm({
                  title: '',
                  price: 0,
                  description: '',
                  duration_hours: 1,
                  use_traction_compound: false,
                  is_active: true,
                  track_type: selectedTrackType,
                  deposit_percentage: 30,
                  blocks_full_day: false
                });
                setShowPricingModal(true);
              }}
              className="text-primary-500 hover:text-primary-400 flex items-center gap-2"
            >
              <Plus className="h-5 w-5" />
              Add Service Option
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPricing.map((item) => (
              <div key={item.id} className="bg-zinc-900 rounded-lg p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-white">{item.title}</h3>
                    <p className="text-gray-400 mt-1">{item.description}</p>
                    <p className="text-primary-500 font-semibold mt-2">{formatCurrency(item.price)}</p>
                    <p className="text-green-500 text-sm mt-1">
                      Deposit: {formatCurrency(item.deposit_amount)} ({item.deposit_percentage}%)
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {item.duration_hours} {item.duration_hours === 1 ? 'Hour' : 'Hours'}
                      </span>
                      {item.use_traction_compound && (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Traction Compound
                        </span>
                      )}
                      {item.blocks_full_day && (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                          Full Day Booking
                        </span>
                      )}
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        item.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {item.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setSelectedPricing(item);
                        setPricingForm({
                          title: item.title,
                          price: item.price,
                          description: item.description,
                          duration_hours: item.duration_hours,
                          use_traction_compound: item.use_traction_compound,
                          is_active: item.is_active,
                          track_type: item.track_type,
                          deposit_percentage: item.deposit_percentage || 30,
                          blocks_full_day: item.blocks_full_day || false
                        });
                        setShowPricingModal(true);
                      }}
                      className="text-gray-400 hover:text-white"
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDeletePricing(item.id)}
                      className="text-gray-400 hover:text-primary-500"
                    >
                      <Trash className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-12">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">Add-on Services</h2>
            <button
              onClick={() => {
                setSelectedAddOn(null);
                setAddOnForm({
                  name: '',
                  description: '',
                  price: 0,
                  is_active: true
                });
                setShowAddOnModal(true);
              }}
              className="text-primary-500 hover:text-primary-400 flex items-center gap-2"
            >
              <Plus className="h-5 w-5" />
              Add Service
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAddOns.map((item) => (
              <div key={item.id} className="bg-zinc-900 rounded-lg p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-white">{item.name}</h3>
                    <p className="text-gray-400 mt-1">{item.description}</p>
                    <p className="text-primary-500 font-semibold mt-2">{formatCurrency(item.price)}</p>
                    <span className={`mt-2 inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                      item.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {item.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setSelectedAddOn(item);
                        setAddOnForm({
                          name: item.name,
                          description: item.description,
                          price: item.price,
                          is_active: item.is_active
                        });
                        setShowAddOnModal(true);
                      }}
                      className="text-gray-400 hover:text-white"
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteAddOn(item.id)}
                      className="text-gray-400 hover:text-primary-500"
                    >
                      <Trash className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* System Settings Modal */}
        {showSettingsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-zinc-900 rounded-lg p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-white">Lighting Settings</h2>
                <button onClick={() => setShowSettingsModal(false)}>
                  <X className="h-6 w-6 text-gray-400 hover:text-white" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Additional fee for lighting after threshold time
                  </label>
                  <input
                    type="number"
                    value={settingsForm.lighting_fee_amount}
                    onChange={(e) => setSettingsForm({ ...settingsForm, lighting_fee_amount: e.target.value })}
                    step="0.01"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                  />
                </div>
                
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settingsForm.lighting_fee_enabled}
                      onChange={(e) => setSettingsForm({ ...settingsForm, lighting_fee_enabled: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-300">
                      Whether lighting fees are enabled
                    </span>
                  </label>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Time after which lighting fee applies (24-hour format)
                  </label>
                  <input
                    type="time"
                    value={settingsForm.lighting_fee_threshold}
                    onChange={(e) => setSettingsForm({ ...settingsForm, lighting_fee_threshold: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                  />
                </div>
              </div>
              
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setShowSettingsModal(false)}
                  className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-2 rounded-md font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSystemSettingsSubmit}
                  className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-2 rounded-md font-semibold"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Track Info Modal */}
        {showTrackInfoModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-zinc-900 rounded-lg p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-white">
                  Edit Track Information
                </h2>
                <button onClick={() => setShowTrackInfoModal(false)}>
                  <X className="h-6 w-6 text-gray-400 hover:text-white" />
                </button>
              </div>
              <form onSubmit={handleTrackInfoSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    value={trackInfoForm.title}
                    onChange={(e) => setTrackInfoForm({ ...trackInfoForm, title: e.target.value })}
                    required
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={trackInfoForm.description}
                    onChange={(e) => setTrackInfoForm({ ...trackInfoForm, description: e.target.value })}
                    required
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white h-32"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Features
                  </label>
                  <div className="space-y-2">
                    {trackInfoForm.features.map((feature, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          value={feature}
                          onChange={(e) => updateFeature(index, e.target.value)}
                          required
                          className="flex-1 bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                        />
                        <button
                          type="button"
                          onClick={() => removeFeature(index)}
                          className="text-gray-400 hover:text-primary-500"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addFeature}
                      className="text-primary-500 hover:text-primary-400 text-sm font-semibold"
                    >
                      Add Feature
                    </button>
                  </div>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={trackInfoForm.is_active}
                    onChange={(e) => setTrackInfoForm({ ...trackInfoForm, is_active: e.target.checked })}
                    className="mr-2"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-gray-300">
                    Active
                  </label>
                </div>
                <button
                  type="submit"
                  className="w-full bg-primary-600 hover:bg-primary-700 text-white py-2 rounded-md font-semibold"
                >
                  Update Track Information
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Pricing Modal */}
        {showPricingModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-zinc-900 rounded-lg p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-white">
                  {selectedPricing ? 'Edit Service' : 'Add Service'}
                </h2>
                <button onClick={() => setShowPricingModal(false)}>
                  <X className="h-6 w-6 text-gray-400 hover:text-white" />
                </button>
              </div>
              <form onSubmit={handlePricingSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    value={pricingForm.title}
                    onChange={(e) => setPricingForm({ ...pricingForm, title: e.target.value })}
                    required
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Price
                  </label>
                  <input
                    type="number"
                    value={pricingForm.price}
                    onChange={(e) => setPricingForm({ ...pricingForm, price: parseFloat(e.target.value) })}
                    required
                    min="0"
                    step="0.01"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={pricingForm.description}
                    onChange={(e) => setPricingForm({ ...pricingForm, description: e.target.value })}
                    required
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white h-32"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Duration (Hours)
                  </label>
                  <select
                    value={pricingForm.duration_hours}
                    onChange={(e) => {
                      const hours = parseInt(e.target.value);
                      setPricingForm({ 
                        ...pricingForm, 
                        duration_hours: hours,
                        blocks_full_day: hours === 8
                      });
                    }}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                  >
                    <option value={1}>1 Hour</option>
                    <option value={2}>2 Hours</option>
                    <option value={4}>4 Hours</option>
                    <option value={8}>8 Hours (Full Day)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Deposit Percentage
                  </label>
                  <input
                    type="number"
                    value={pricingForm.deposit_percentage}
                    onChange={(e) => setPricingForm({ ...pricingForm, deposit_percentage: parseInt(e.target.value) })}
                    required
                    min="0"
                    max="100"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                  />
                  <p className="text-sm text-gray-400 mt-1">
                    Deposit amount: {formatCurrency((pricingForm.price * pricingForm.deposit_percentage) / 100)}
                  </p>
                </div>
                
                {selectedTrackType === 'drag' && (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="use_traction_compound"
                      checked={pricingForm.use_traction_compound}
                      onChange={(e) => setPricingForm({ ...pricingForm, use_traction_compound: e.target.checked })}
                      className="mr-2"
                    />
                    <label htmlFor="use_traction_compound" className="text-sm font-medium text-gray-300">
                      Include Traction Compound
                    </label>
                  </div>
                )}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="blocks_full_day"
                    checked={pricingForm.blocks_full_day}
                    onChange={(e) => setPricingForm({ ...pricingForm, blocks_full_day: e.target.checked })}
                    className="mr-2"
                  />
                  <label htmlFor="blocks_full_day" className="text-sm font-medium text-gray-300">
                    Blocks Full Day (prevents other bookings)
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={pricingForm.is_active}
                    onChange={(e) => setPricingForm({ ...pricingForm, is_active: e.target.checked })}
                    className="mr-2"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-gray-300">
                    Active
                  </label>
                </div>
                <button
                  type="submit"
                  className="w-full bg-primary-600 hover:bg-primary-700 text-white py-2 rounded-md font-semibold"
                >
                  {selectedPricing ? 'Update Service' : 'Create Service'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Add-on Modal */}
        {showAddOnModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-zinc-900 rounded-lg p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-white">
                  {selectedAddOn ? 'Edit Add-on Service' : 'Add Add-on Service'}
                </h2>
                <button onClick={() => setShowAddOnModal(false)}>
                  <X className="h-6 w-6 text-gray-400 hover:text-white" />
                </button>
              </div>
              <form onSubmit={handleAddOnSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={addOnForm.name}
                    onChange={(e) => setAddOnForm({ ...addOnForm, name: e.target.value })}
                    required
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={addOnForm.description}
                    onChange={(e) => setAddOnForm({ ...addOnForm, description: e.target.value })}
                    required
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white h-32"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Price
                  </label>
                  <input
                    type="number"
                    value={addOnForm.price}
                    onChange={(e) => setAddOnForm({ ...addOnForm, price: parseFloat(e.target.value) })}
                    required
                    min="0"
                    step="0.01"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="add_on_is_active"
                    checked={addOnForm.is_active}
                    onChange={(e) => setAddOnForm({ ...addOnForm, is_active: e.target.checked })}
                    className="mr-2"
                  />
                  <label htmlFor="add_on_is_active" className="text-sm font-medium text-gray-300">
                    Active
                  </label>
                </div>
                <button
                  type="submit"
                  className="w-full bg-primary-600 hover:bg-primary-700 text-white py-2 rounded-md font-semibold"
                >
                  {selectedAddOn ? 'Update Add-on Service' : 'Create Add-on Service'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}